import React, { useState } from 'react';
import { Zap, Activity, Play, AlertTriangle } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

type EvAnalysisType = 'burst' | 'demag' | 'insulation' | 'pdiv' | 'thermal_transient' | 'edm' | 'back_emf';

const EvMotorAnalysis: React.FC = () => {
    const [analysisType, setAnalysisType] = useState<EvAnalysisType>('burst');
    const [params, setParams] = useState<{ [key: string]: string | number }>({});
    const [result, setResult] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    const analysisOptions: { value: EvAnalysisType, label: string }[] = [
        { value: 'burst', label: 'Rotor Burst Speed' },
        { value: 'demag', label: 'Demagnetization Risk' },
        { value: 'insulation', label: 'Insulation Lifetime' },
        { value: 'pdiv', label: 'Partial Discharge (PDIV)' },
        { value: 'thermal_transient', label: 'Thermal Transient' },
        { value: 'edm', label: 'Bearing EDM Risk' },
        { value: 'back_emf', label: 'Back-EMF Safety' },
    ];

    const getFields = (type: EvAnalysisType) => {
        switch (type) {
            case 'burst': return [
                { key: 'rpm', label: 'Speed (RPM)', type: 'number', placeholder: '14000' },
                { key: 'radius_mm', label: 'Rotor Radius (mm)', type: 'number', placeholder: '80' },
                { key: 'density_kgm3', label: 'Material Density (kg/m^3)', type: 'number', placeholder: '7600' },
                { key: 'yield_strength_mpa', label: 'Yield Strength (MPa)', type: 'number', placeholder: '400' }
            ];
            case 'demag': return [
                { key: 'temp_c', label: 'Magnet Temp (°C)', type: 'number', placeholder: '120' },
                { key: 'peak_current_a', label: 'Peak Current (A)', type: 'number', placeholder: '400' },
                { key: 'turns', label: 'Turns per Phase', type: 'number', placeholder: '10' },
                { key: 'path_length_mm', label: 'Magnetic Path Length (mm)', type: 'number', placeholder: '100' }
            ];
            case 'insulation': return [
                { key: 'voltage_v', label: 'Voltage (V)', type: 'number', placeholder: '650' },
                { key: 'temp_c', label: 'Winding Temp (°C)', type: 'number', placeholder: '150' },
                { key: 'freq_khz', label: 'Switching Freq (kHz)', type: 'number', placeholder: '10' }
            ];
            case 'pdiv': return [
                { key: 'voltage_v', label: 'DC Bus Voltage (V)', type: 'number', placeholder: '800' },
                { key: 'cable_m', label: 'Cable Length (m)', type: 'number', placeholder: '5' },
                { key: 'rise_ns', label: 'Rise Time (ns)', type: 'number', placeholder: '50' },
                { key: 'thick_mm', label: 'Insulation Thickness (mm)', type: 'number', placeholder: '0.1' }
            ];
            case 'thermal_transient': return [
                { key: 'current_a', label: 'Current (A)', type: 'number', placeholder: '200' },
                { key: 'duration_s', label: 'Duration (s)', type: 'number', placeholder: '60' },
                { key: 'resistance_ohms', label: 'Resistance (Ohms)', type: 'number', placeholder: '0.05' },
                { key: 'mass_j_c', label: 'Thermal Mass (J/°C)', type: 'number', placeholder: '500' },
                { key: 'cooling_c_w', label: 'Cooling Resistance (°C/W)', type: 'number', placeholder: '0.8' }
            ];
            case 'edm': return [
                { key: 'dc_bus_voltage', label: 'DC Bus Voltage (V)', type: 'number', placeholder: '400' },
                { key: 'bearing_voltage_ratio', label: 'Bearing Voltage Ratio (BVR)', type: 'number', placeholder: '0.05' },
                { key: 'grease_breakdown_voltage', label: 'Grease Breakdown (V)', type: 'number', placeholder: '15' }
            ];
            case 'back_emf': return [
                { key: 'max_overspeed_rpm', label: 'Max Overspeed (RPM)', type: 'number', placeholder: '12000' },
                { key: 'ke_voltage_constant', label: 'Ke (V/rpm)', type: 'number', placeholder: '0.05' },
                { key: 'inverter_dc_bus_voltage', label: 'Inverter Rating (V)', type: 'number', placeholder: '400' }
            ];
            default: return [];
        }
    };

    const handleCalculate = async () => {
        setLoading(true);
        setResult(null);
        try {
            const payload = {
                analysis_type: analysisType,
                parameters: Object.entries(params).reduce((acc, [k, v]) => ({
                    ...acc,
                    [k]: typeof v === 'string' && !isNaN(parseFloat(v)) ? parseFloat(v) : v
                }), {})
            };
            const data = await authenticatedFetchJSON('/api/v1/lab/ev-motor-risk', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setResult(data);
        } catch (error) {
            console.error(error);
            setResult({ error: "Analysis failed. Check backend." });
        } finally {
            setLoading(false);
        }
    };

    const currentFields = getFields(analysisType);

    return (
        <div className="bg-card border border-border p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Zap className="text-yellow-500" /> EV Motor Safety
                </h2>
                <select
                    value={analysisType}
                    onChange={(e) => {
                        setAnalysisType(e.target.value as EvAnalysisType);
                        setParams({});
                        setResult(null);
                    }}
                    className="bg-background border border-border rounded px-2 py-1 text-sm outline-none focus:border-primary"
                >
                    {analysisOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    {currentFields.map(field => (
                        <div key={field.key}>
                            <label className="block text-xs font-medium mb-1 text-text-secondary">{field.label}</label>
                            <input
                                type="number"
                                className="w-full bg-background border border-border rounded p-2 text-sm focus:ring-1 focus:ring-primary"
                                value={params[field.key] || ''}
                                onChange={e => setParams(prev => ({ ...prev, [field.key]: e.target.value }))}
                                placeholder={field.placeholder?.toString()}
                            />
                        </div>
                    ))}

                    <button
                        onClick={handleCalculate}
                        disabled={loading}
                        className="w-full bg-primary text-white py-2 rounded flex justify-center items-center gap-2 hover:bg-primary/90 mt-4 disabled:opacity-50"
                    >
                        {loading ? <Activity className="animate-spin" /> : <Play size={16} />}
                        Run Safety Check
                    </button>
                </div>

                <div className="bg-muted/20 border border-border rounded p-4 h-full min-h-[200px]">
                    <h3 className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">Analysis Results</h3>
                    {result ? (
                        <div className="space-y-3">
                            {/* Safety Header */}
                            {result.result?.includes("PASS") || result.safety_factor > 1.2 || result.risk === 'low' ? (
                                <div className="bg-green-100 text-green-800 p-2 rounded flex items-center gap-2 font-bold text-sm">
                                    <Activity size={16} /> PASS / LOW RISK
                                </div>
                            ) : null}

                            {(result.result?.includes("FAIL") || (result.safety_factor && result.safety_factor <= 1.0) || result.risk === 'high') && (
                                <div className="bg-red-100 text-red-800 p-2 rounded flex items-center gap-2 font-bold text-sm">
                                    <AlertTriangle size={16} /> FAIL / HIGH RISK
                                </div>
                            )}

                            {/* Key-Value Breakdown */}
                            <div className="space-y-1">
                                {Object.entries(result).map(([k, v]) => (
                                    <div key={k} className="flex justify-between border-b border-border/50 py-1 last:border-0">
                                        <span className="text-xs text-text-secondary capitalize">{k.replace(/_/g, ' ')}</span>
                                        <span className="text-sm font-mono text-text-primary text-right">{
                                            typeof v === 'number' ? v.toFixed(4) : String(v)
                                        }</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-text-secondary opacity-50">
                            <Zap size={32} className="mb-2" />
                            <p className="text-xs">Configure motor parameters to analyze.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EvMotorAnalysis;
